import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EDITOR_ROLES, SUPERVISOR_ROLES } from "@shared/core/types";
import {
  CANDIDATE_STATUSES,
  CANDIDATE_STATUS_LABELS,
  EDUCATION_LABELS,
  SEX_LABELS,
  type Candidate,
  type CandidateEvent,
} from "@shared/modules/x-candidats";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Modal } from "../../../core/components/ui/modal";
import { Select } from "../../../core/components/ui/input";
import { Icon } from "../../../core/components/ui/icon";
import { formatDateTime } from "../../../core/lib/format";
import { allowedFor } from "../../../core/modules";
import { moduleEnabled } from "../../../core/blueprint";
import { assignLawyer, candidateHistory, deleteCandidate, getCandidate, lawyerOptions, unassignLawyer, updateCandidate } from "../lib/api";
import { StatusBadge } from "../components/status-badge";
import { CandidateForm, draftFrom, toInput } from "../components/candidate-form";

const EVENT_LABELS: Record<string, string> = {
  "dossier.cree": "Dossier créé",
  "dossier.modifie": "Fiche modifiée",
  "dossier.statut_change": "Statut changé",
  "dossier.supprime": "Dossier supprimé",
  "dossier.avocat_attribue": "Avocat attribué",
  "dossier.avocat_retire": "Avocat retiré",
};

export function CandidateDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const [c, setC] = useState<Candidate | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [history, setHistory] = useState<CandidateEvent[]>([]);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lawyers, setLawyers] = useState<{ id: string; name: string; email: string }[]>([]);
  const [lawyerPick, setLawyerPick] = useState("");
  const [busy, setBusy] = useState(false);
  const canEdit = allowedFor({ roles: EDITOR_ROLES }, user?.role);
  const canSupervise = allowedFor({ roles: SUPERVISOR_ROLES }, user?.role);
  const isLawyer = user?.role === "avocat";

  const load = useCallback(
    async (reveal = false) => {
      try {
        const [cand, hist] = await Promise.all([getCandidate(id, reveal), candidateHistory(id)]);
        setC(cand);
        setHistory(hist);
        setRevealed(reveal);
      } catch {
        push("danger", "Dossier introuvable");
        navigate("/candidats");
      }
    },
    [id, navigate, push],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(() => {
    if (canSupervise) lawyerOptions().then(setLawyers).catch(() => setLawyers([]));
  }, [canSupervise]);

  if (!c) return <div className="page">Chargement…</div>;

  const changeStatus = async (status: string) => {
    setBusy(true);
    try {
      await updateCandidate(c.id, { status: status as Candidate["status"] });
      push("success", `Statut : ${CANDIDATE_STATUS_LABELS[status as Candidate["status"]]}`);
      await load(revealed);
    } catch {
      push("danger", "Changement impossible");
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    if (!lawyerPick) return;
    setBusy(true);
    try {
      await assignLawyer(c.id, lawyerPick);
      push("success", "Avocat attribué");
      setLawyerPick("");
      await load(revealed);
    } catch {
      push("danger", "Attribution impossible");
    } finally {
      setBusy(false);
    }
  };

  const fullName = `${c.lastName.toUpperCase()} ${c.firstName}`;

  return (
    <div className="page">
      <PageHeader
        title={fullName}
        subtitle={`${c.reference} · créé le ${formatDateTime(c.createdAt, { dateStyle: "long" })}`}
        actions={
          <>
            {moduleEnabled("x-documents") ? (
              <Link className="btn btn--ghost" to={`/candidats/${c.id}/pieces`}>
                <span className="btn__icon">
                  <Icon name="file" size={16} />
                </span>
                Pièces
              </Link>
            ) : null}
            {moduleEnabled("x-avocats") && !isLawyer ? (
              <Link className="btn btn--ghost" to={`/candidats/${c.id}/partages`}>
                <span className="btn__icon">
                  <Icon name="shield" size={16} />
                </span>
                Liens sécurisés
              </Link>
            ) : null}
            {canEdit ? (
              <Button variant="primary" icon={<Icon name="edit" size={16} />} onClick={async () => { if (!revealed) await load(true); setEditing(true); }}>
                Modifier
              </Button>
            ) : null}
          </>
        }
      />

      <div className="candidat-detail">
        <Card
          title="Statut"
          actions={<StatusBadge status={c.status} />}
        >
          {canEdit ? (
            <Select value={c.status} disabled={busy} onChange={(e) => void changeStatus(e.target.value)}>
              {CANDIDATE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {CANDIDATE_STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          ) : (
            <p className="muted">Le statut est modifié par l'équipe de recrutement.</p>
          )}
        </Card>

        <Card
          title="Identité"
          actions={
            !isLawyer ? (
              <Button variant="ghost" size="sm" onClick={() => void load(!revealed)} icon={<Icon name="eye" size={14} />}>
                {revealed ? "Masquer les données sensibles" : "Afficher les données sensibles"}
              </Button>
            ) : undefined
          }
        >
          <dl className="kv">
            <dt>Sexe</dt>
            <dd>{c.sex ? SEX_LABELS[c.sex] : "—"}</dd>
            <dt>Âge</dt>
            <dd>{c.age ?? "—"}{c.birthYear ? ` (né·e en ${c.birthYear})` : ""}</dd>
            <dt>Date de naissance</dt>
            <dd>{revealed ? (c.birthDate ?? "—") : <span className="muted">●●●●-●●-●●</span>}</dd>
            <dt>Nationalité</dt>
            <dd>{c.nationality ?? "—"}</dd>
            <dt>Résidence</dt>
            <dd>{[c.city, c.countryResidence].filter(Boolean).join(", ") || "—"}</dd>
            <dt>Courriel</dt>
            <dd>{c.email ?? "—"}</dd>
            <dt>Téléphone</dt>
            <dd>{c.phone ?? "—"}</dd>
            <dt>Passeport</dt>
            <dd>
              {revealed ? (c.passportNumber ?? "—") : <span className="muted">●●●●●●●●</span>}
              {c.passportExpiresAt ? <span className="muted"> · expire le {c.passportExpiresAt}</span> : null}
            </dd>
          </dl>
        </Card>

        <Card title="Profil professionnel">
          <dl className="kv">
            <dt>Profession</dt>
            <dd>{c.profession ?? "—"}</dd>
            <dt>Études</dt>
            <dd>
              {c.educationLevel ? EDUCATION_LABELS[c.educationLevel] : "—"}
              {c.educationDetail ? <span className="muted"> · {c.educationDetail}</span> : null}
            </dd>
            <dt>Expérience</dt>
            <dd>{c.experienceYears != null ? `${c.experienceYears} an${c.experienceYears > 1 ? "s" : ""}` : "—"}</dd>
            <dt>Langues</dt>
            <dd>{c.languages.length ? c.languages.join(", ") : "—"}</dd>
          </dl>
          {c.notes ? (
            <>
              <h4 className="candidat-detail__notes-title">Notes</h4>
              <p className="candidat-detail__notes">{c.notes}</p>
            </>
          ) : null}
        </Card>

        {!isLawyer ? (
          <Card title="Avocats" subtitle="Seuls les avocats attribués voient ce dossier.">
            <ul className="candidat-detail__lawyers">
              {(c.assignedLawyers ?? []).map((l) => (
                <li key={l.userId}>
                  <span>
                    {l.name} <span className="muted">({l.email})</span>
                  </span>
                  {canSupervise ? (
                    <Button variant="ghost-danger" size="sm" disabled={busy} onClick={async () => { await unassignLawyer(c.id, l.userId); await load(revealed); }}>
                      Retirer
                    </Button>
                  ) : null}
                </li>
              ))}
              {(c.assignedLawyers ?? []).length === 0 ? <li className="muted">Aucun avocat attribué.</li> : null}
            </ul>
            {canSupervise ? (
              <div className="candidat-detail__assign">
                <Select value={lawyerPick} onChange={(e) => setLawyerPick(e.target.value)}>
                  <option value="">Choisir un avocat…</option>
                  {lawyers
                    .filter((l) => !(c.assignedLawyers ?? []).some((a) => a.userId === l.id))
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} — {l.email}
                      </option>
                    ))}
                </Select>
                <Button variant="primary" size="sm" disabled={!lawyerPick || busy} onClick={assign}>
                  Attribuer
                </Button>
              </div>
            ) : null}
          </Card>
        ) : null}

        <Card title="Historique">
          <ul className="candidat-detail__history">
            {history.map((h) => (
              <li key={h.id}>
                <span className="candidat-detail__history-when">{formatDateTime(h.at, { dateStyle: "short", timeStyle: "short" })}</span>
                <span className="candidat-detail__history-what">
                  {EVENT_LABELS[h.type] ?? h.type}
                  {h.type === "dossier.statut_change" && h.details ? (
                    <span className="muted">
                      {" "}
                      : {CANDIDATE_STATUS_LABELS[h.details.de as Candidate["status"]] ?? String(h.details.de)} →{" "}
                      {CANDIDATE_STATUS_LABELS[h.details.vers as Candidate["status"]] ?? String(h.details.vers)}
                    </span>
                  ) : null}
                </span>
                <span className="candidat-detail__history-who muted">{h.userName ?? "système"}</span>
              </li>
            ))}
            {history.length === 0 ? <li className="muted">Aucun événement.</li> : null}
          </ul>
        </Card>

        {canSupervise ? (
          <Card title="Zone sensible">
            <p className="muted">La suppression retire le dossier des listes ; l'historique et le journal restent.</p>
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Supprimer ce dossier
            </Button>
          </Card>
        ) : null}
      </div>

      <Modal open={editing} title={`Modifier ${c.reference}`} onClose={() => setEditing(false)} size="lg">
        <CandidateForm
          initial={draftFrom(c)}
          submitLabel="Enregistrer"
          busy={busy}
          onCancel={() => setEditing(false)}
          onSubmit={async (d) => {
            setBusy(true);
            try {
              await updateCandidate(c.id, toInput(d));
              push("success", "Fiche enregistrée");
              setEditing(false);
              await load(revealed);
            } catch {
              push("danger", "Enregistrement impossible : vérifiez les champs.");
            } finally {
              setBusy(false);
            }
          }}
        />
      </Modal>

      <Modal
        open={confirmDelete}
        title="Supprimer ce dossier ?"
        onClose={() => setConfirmDelete(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await deleteCandidate(c.id);
                  push("success", "Dossier supprimé");
                  navigate("/candidats");
                } catch {
                  push("danger", "Suppression impossible");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Supprimer
            </Button>
          </>
        }
      >
        <p>
          {fullName} ({c.reference}) disparaîtra des listes et des recherches. Cette action est journalisée.
        </p>
      </Modal>
    </div>
  );
}
